import { useState } from "react";
import { Languages, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LANGUAGES, baseDict } from "@/i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const TARGETS = LANGUAGES.filter((l) => l.code !== "pl");
const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";

export function useAiTranslate() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const run = async (items) => {
    if (!items.length) return toast.error("Brak zmienionych polskich tekstów do przetłumaczenia.");
    setLoading(true);
    try {
      const chunks = [];
      for (let i = 0; i < items.length; i += 25) chunks.push(items.slice(i, i + 25));
      const merged = {};
      for (const c of chunks) {
        const r = await api.post("/admin/i18n/translate", { items: c });
        Object.assign(merged, r.data.translations);
      }
      setResult({ items, translations: merged });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Tłumaczenie nie powiodło się.");
    } finally {
      setLoading(false);
    }
  };
  return { loading, result, setResult, run };
}

export function TranslateDialog({ result, onClose, onApply }) {
  const [edits, setEdits] = useState({});
  if (!result) return null;
  const val = (path, code) => edits[`${path}|${code}`] ?? result.translations[path]?.[code] ?? "";
  const apply = () => {
    const out = {};
    for (const { path } of result.items) {
      for (const l of TARGETS) {
        const v = val(path, l.code).trim();
        const current = path.split(".").reduce((a, k) => a?.[k], baseDict(l.code));
        if (v && v !== current) (out[l.code] ||= {})[path] = v;
      }
    }
    onApply(out);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="translate-dialog" className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Propozycje tłumaczeń AI</DialogTitle>
          <DialogDescription>Sprawdź i popraw tłumaczenia, potem „Wstaw do tekstów”. Zmiany trafią do pól EN/DE/FR/UK/TR jako niezapisane — na koniec kliknij „Zapisz teksty”.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {result.items.map(({ path, text }) => (
            <div key={path} data-testid={`translate-item-${path}`} className="rounded-xl border border-border p-4 space-y-2">
              <code className="font-mono text-[11px] text-muted-foreground break-all">{path}</code>
              <p className="text-sm font-semibold whitespace-pre-line">{text}</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {TARGETS.map((l) => {
                  const base = baseDict(l.code);
                  const current = path.split(".").reduce((a, k) => a?.[k], base);
                  return (
                    <div key={l.code}>
                      <div className="flex items-baseline justify-between"><span className="text-[11px] font-semibold text-muted-foreground">{l.label}</span>{current && <span className="text-[10px] text-muted-foreground truncate max-w-[60%]" title={current}>obecnie: {current}</span>}</div>
                      <Textarea data-testid={`translate-${path}-${l.code}`} value={val(path, l.code)} onChange={(e) => setEdits({ ...edits, [`${path}|${l.code}`]: e.target.value })} className="bg-background text-sm min-h-[52px]" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={`${btn} border border-border hover:bg-muted`}>Anuluj</button>
          <button type="button" data-testid="translate-apply-button" onClick={apply} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}><Check size={13} /> Wstaw do tekstów</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TranslateButton({ loading, onClick, count, small = false }) {
  return (
    <button type="button" data-testid={small ? "i18n-translate-field" : "i18n-translate-all"} onClick={onClick} disabled={loading}
      className={small ? "block mt-1 text-[11px] text-brand-teal hover:underline inline-flex items-center gap-1 disabled:opacity-60" : `${btn} bg-brand-navy text-white hover:bg-brand-teal dark:bg-white dark:text-slate-900`}>
      {loading ? <Loader2 size={small ? 10 : 13} className="animate-spin" /> : <Languages size={small ? 10 : 13} />}
      {small ? "przetłumacz AI (5 języków)" : `Przetłumacz zmienione PL na 5 języków${count ? ` (${count})` : ""}`}
    </button>
  );
}
